import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageCircleQuestion, Send, X } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useLandingLang } from "@/components/FundingCaseStudy";

const T = {
  en: { open: "Questions before you start?", title: "Ask about price, trust or how it works", hello: "Hi. Ask me anything about cost, how the data works, or what happens if the AI gets something wrong. Straight answers only.",
    chips: ["Is it really free to start?", "Why trust a new platform?", "What if the AI gets it wrong?", "How do I cancel?"], ph: "Type your question", note: "AI answers. Not legal or tax advice." },
  es: { open: "¿Preguntas antes de empezar?", title: "Pregunte sobre precio, confianza o cómo funciona", hello: "Hola. Pregúnteme sobre costos, cómo funcionan los datos o qué pasa si la IA se equivoca. Solo respuestas directas.",
    chips: ["¿De verdad es gratis empezar?", "¿Por qué confiar en una plataforma nueva?", "¿Y si la IA se equivoca?", "¿Cómo cancelo?"], ph: "Escriba su pregunta", note: "Respuestas de IA. No es asesoría legal ni fiscal." },
};

const sid = () => {
  let s = sessionStorage.getItem("gv_trust_sid");
  if (!s) { s = `ta_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; sessionStorage.setItem("gv_trust_sid", s); }
  return s;
};

function Inline({ text }) {
  return text.split(/(\[[^\]]+\]\(\/[^)]*\)|\*\*[^*]+\*\*)/g).map((p, i) => {
    const l = p.match(/^\[([^\]]+)\]\((\/[^)]*)\)$/);
    if (l) return <Link key={i} to={l[2]} className="font-semibold text-emerald-700 underline underline-offset-2">{l[1]}</Link>;
    if (/^\*\*[^*]+\*\*$/.test(p)) return <b key={i}>{p.slice(2, -2)}</b>;
    return <span key={i}>{p}</span>;
  });
}

function Reply({ text }) {
  return <div className="space-y-1.5">{text.split("\n").filter((l) => l.trim()).map((l, i) => (/^\s*[-*•]\s/.test(l)
    ? <div key={i} className="flex gap-2"><span className="text-emerald-600">•</span><span><Inline text={l.replace(/^\s*[-*•]\s/, "")} /></span></div>
    : <p key={i}><Inline text={l} /></p>))}</div>;
}

const load = (k, d) => { try { return JSON.parse(sessionStorage.getItem(k)) ?? d; } catch { return d; } };

function Panel({ L, lang, onClose }) {
  const [msgs, setMsgs] = useState(() => load("gv_trust_msgs", []));
  useEffect(() => { sessionStorage.setItem("gv_trust_msgs", JSON.stringify(msgs.slice(-20))); }, [msgs]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const ask = (text) => {
    const m = text.trim();
    if (!m || busy) return;
    setMsgs((x) => [...x, { me: true, t: m }]); setQ(""); setBusy(true);
    api.post("/public/assistant", { session_id: sid(), message: m, lang })
      .then((r) => setMsgs((x) => [...x, { t: r.data.reply }]))
      .catch((e) => setMsgs((x) => [...x, { t: formatErr(e), err: true }])).finally(() => setBusy(false));
  };
  return (
    <div className="fixed z-[9990] bottom-20 left-3 right-3 sm:right-auto sm:left-6 sm:w-[380px] max-h-[70vh] flex flex-col bg-white border border-slate-200 rounded-xl shadow-2xl" style={{ animation: "fadeUp 250ms ease" }} role="dialog" aria-label={L.title} data-no-translate data-testid="trust-assistant-panel">
      <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-100 bg-slate-900 text-white rounded-t-xl">
        <div className="text-sm font-semibold">{L.title}</div>
        <button onClick={onClose} aria-label="Close" className="text-slate-300 hover:text-white" data-testid="trust-assistant-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="trust-assistant-messages">
        <div className="bg-slate-100 rounded-lg p-3 text-slate-700">{L.hello}</div>
        {!msgs.length && <div className="flex flex-wrap gap-2">{L.chips.map((c, i) => <button key={c} onClick={() => ask(c)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 transition-colors" data-testid={`trust-assistant-chip-${i}`}>{c}</button>)}</div>}
        {msgs.map((m, i) => <div key={i} className={m.me ? "ml-8 bg-emerald-600 text-white rounded-lg p-3" : `mr-4 rounded-lg p-3 ${m.err ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-800"}`} data-testid={m.me ? `trust-assistant-q-${i}` : `trust-assistant-a-${i}`}>{m.me ? m.t : <Reply text={m.t} />}</div>)}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" data-testid="trust-assistant-loading" />}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="p-3 border-t border-slate-100 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} maxLength={800} placeholder={L.ph} aria-label={L.ph} className="flex-1 text-sm border border-slate-300 rounded-full px-4 py-2 focus:outline-none focus:border-emerald-500" data-testid="trust-assistant-input" />
        <button type="submit" disabled={busy || !q.trim()} aria-label="Send" className="h-9 w-9 rounded-full bg-emerald-600 text-white grid place-items-center disabled:opacity-40" data-testid="trust-assistant-send"><Send className="h-4 w-4" /></button>
      </form>
      <p className="px-4 pb-3 text-[11px] text-slate-400">{L.note}</p>
    </div>
  );
}

export function TrustAssistant() {
  const lang = useLandingLang();
  const L = T[lang];
  const [open, setOpen] = useState(() => load("gv_trust_open", false));
  useEffect(() => { sessionStorage.setItem("gv_trust_open", JSON.stringify(open)); }, [open]);
  return (
    <>
      {open && <Panel L={L} lang={lang} onClose={() => setOpen(false)} />}
      <button onClick={() => setOpen((o) => !o)} className="fixed z-[9990] bottom-4 left-3 sm:left-6 flex items-center gap-2 bg-slate-900 text-white rounded-full pl-3 pr-4 py-2.5 shadow-xl hover:bg-slate-800 transition-colors" aria-expanded={open} data-no-translate data-testid="trust-assistant-button">
        <MessageCircleQuestion className="h-5 w-5 text-emerald-400" /><span className="text-sm font-medium hidden sm:inline">{L.open}</span>
      </button>
    </>
  );
}
